/** Shared play-session toolbar layout & action chip styles (MUI `sx`). */

export const playToolbarSystemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

/** Default Android PlayToolbar chip; recording capture uses the larger size. */
export const PLAY_TOOLBAR_BTN_SIZE = 40;
export const PLAY_TOOLBAR_BTN_SIZE_RECORDING = 64;
export const PLAY_TOOLBAR_ICON_SIZE = 22;
export const PLAY_TOOLBAR_ICON_SIZE_RECORDING = 34;

/**
 * Outer toolbar chrome.
 *
 * `vertical` turns the bar into a rail down the side of the screen — landscape has width to spare
 * and no height, so the controls stack instead of eating a row above the board.
 */
export function playToolbarBarSx(tokens, { vertical = false, large = false } = {}) {
    const railMin = large ? 84 : 56;
    return {
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "center",
        justifyContent: vertical ? "center" : "space-between",
        gap: large ? 1.5 : 1.25,
        borderRadius: large ? "18px" : "14px",
        bgcolor: tokens.pillInactiveBg,
        border: tokens.elevatedCardBorder,
        flexShrink: 0,
        boxSizing: "border-box",
        flexWrap: "nowrap",
        overflow: "visible",
        ...(vertical
            ? { mr: 1.25, px: large ? 0.85 : 0.65, py: large ? 1.5 : 1.25, minWidth: railMin }
            : { mb: 1, px: large ? 1.5 : 1.25, py: large ? 0.85 : 0.65, minHeight: railMin }),
    };
}

/** Right cluster: Moves + Hint + Mute + Preview + Settings. */
export function playToolbarActionsGroupSx({ vertical = false, large = false } = {}) {
    return {
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "center",
        justifyContent: vertical ? "center" : "flex-end",
        gap: large ? 1 : 0.75,
        flex: "0 0 auto",
        flexShrink: 0,
    };
}

/** Soft rule between stats and actions — runs across the rail when the toolbar is vertical. */
export function playToolbarDividerSx(tokens, { vertical = false } = {}) {
    return {
        alignSelf: "stretch",
        bgcolor: tokens.divider,
        flexShrink: 0,
        opacity: 0.9,
        ...(vertical ? { height: "1px", mx: 0.35 } : { width: "1px", my: 0.35 }),
    };
}

/**
 * Glass circular tool chip — matches Android `PlayToolbar` (full circle).
 * `glass` overrides the fill (e.g. `rgba(0,0,0,0.04)` / `rgba(255,255,255,0.08)`).
 */
export function playToolbarActionBtnSx(tokens, { glass = null, size = PLAY_TOOLBAR_BTN_SIZE } = {}) {
    const side = Number(size) || PLAY_TOOLBAR_BTN_SIZE;
    return {
        minWidth: side,
        minHeight: side,
        width: side,
        height: side,
        p: 0,
        borderRadius: `${side / 2}px`,
        color: tokens.text,
        bgcolor: glass ?? tokens.pillInactiveBg,
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.15s ease, transform 0.12s ease, opacity 0.15s ease",
        "&:hover": {
            bgcolor: tokens.iconMutedBg,
        },
        "&:active": {
            transform: "scale(0.94)",
        },
        "&.Mui-disabled": {
            color: tokens.textSubtle,
            bgcolor: glass ?? tokens.pillInactiveBg,
            opacity: 0.35,
        },
    };
}

/** Compact pause control next to the timer value. */
export function playToolbarPauseBtnSx(tokens, { active = false } = {}) {
    return {
        ...playToolbarActionBtnSx(tokens),
        minWidth: 32,
        minHeight: 32,
        width: 32,
        height: 32,
        borderRadius: "16px",
        color: active ? "#2EC4B6" : tokens.textMuted,
    };
}

/**
 * Hint lightbulb — circular glass chip with coin count as a corner badge
 * (Android `PlayToolbar` hint + amber badge).
 */
export function playToolbarHintPillSx(
    tokens,
    { active = false, disabled = false, glass = null, size = PLAY_TOOLBAR_BTN_SIZE } = {},
) {
    const side = Number(size) || PLAY_TOOLBAR_BTN_SIZE;
    return {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: side,
        height: side,
        minWidth: side,
        minHeight: side,
        p: 0,
        borderRadius: `${side / 2}px`,
        border: "none",
        cursor: disabled ? "default" : "pointer",
        color: active ? "#2EC4B6" : tokens.text,
        bgcolor: glass ?? tokens.pillInactiveBg,
        boxShadow: active ? "0 0 14px rgba(46, 196, 182, 0.35)" : "none",
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease",
        "&:hover": disabled
            ? {}
            : {
                  bgcolor: tokens.iconMutedBg,
                  color: "#2EC4B6",
              },
        "&:active": disabled
            ? {}
            : {
                  transform: "scale(0.96)",
              },
        "&.Mui-disabled, &[aria-disabled='true']": {
            color: tokens.textSubtle,
            boxShadow: "none",
            opacity: 0.35,
        },
    };
}

/** Amber corner badge on the hint chip (Android `PlayToolbar` styles.badge). */
export function playToolbarHintBadgeSx({ large = false } = {}) {
    return {
        position: "absolute",
        top: large ? -4 : -2,
        right: large ? -6 : -4,
        minWidth: large ? 20 : 16,
        px: large ? "5px" : "4px",
        borderRadius: large ? "10px" : "8px",
        bgcolor: "#F59E0B",
        color: "#1C1917",
        fontFamily: playToolbarSystemFont,
        fontSize: large ? 11 : 9,
        fontWeight: 800,
        lineHeight: 1.35,
        textAlign: "center",
        overflow: "hidden",
    };
}
